import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';

import { informacionPago, limpiarPago } from '../../actions/pagos';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

const InformacionPago = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const informacion = useSelector(state => state.pagos.informacion);

    useEffect(() => {
        if (id) {
            dispatch(informacionPago(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Pagos', path: '/pagos', activo: false },
                { nombre: 'Información del pago', path: `/pagos/informacion/${id}`, activo: true }
            ]));
        }

        return () => {
            dispatch(limpiarPago());
        };
    }, [id]);

    const columnsVueltas = [
        {
            name: 'Folio',
            sortable: true,
            selector: row => row.folio || '-'
        },
        {
            name: 'Fecha',
            sortable: true,
            grow: 1.5,
            selector: row => formatFecha(row.fecha)
        },
        {
            name: 'Total',
            sortable: true,
            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-'
        },
        {
            name: 'Status',
            sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === true ? 'Realizada' : 'Cancelada'}
                </span>
            )
        }
    ];

    return (
        <div className="row mt-5">

            {/* Header */}
            <div className="col-12 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, minWidth: 48 }}>
                        <i className="fas fa-money-bill-wave text-success fs-5"></i>
                    </div>
                    <div>
                        <h4 className="mb-0 fw-semibold">Información del pago</h4>
                        <small className="text-muted">{formatFecha(informacion?.fecha)}</small>
                    </div>
                    <div className="ms-auto">
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/pagos')}>
                            <i className="fas fa-arrow-left me-1"></i> Volver
                        </button>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="col-12 mb-4">
                <div className="row g-3">
                    {[
                        { label: 'Responsable',       value: formatNombre(informacion?.responsable), icon: 'fa-user',           color: 'success'   },
                        { label: 'Vendedor',           value: formatNombre(informacion?.vendedor),    icon: 'fa-user-tag',       color: 'success'   },
                        { label: 'Sucursal',           value: informacion?.sucursal?.nombre || '-',   icon: 'fa-store',          color: 'info'      },
                        { label: 'Evento',             value: informacion?.evento?.nombre || '-',     icon: 'fa-calendar-alt',   color: 'warning'   },
                        { label: 'Método de pago',     value: informacion?.metodo?.nombre || '-',     icon: 'fa-credit-card',    color: 'secondary' },
                        { label: 'Ventas ambulantes',  value: informacion?.vueltas?.length ?? '-',    icon: 'fa-redo',     color: 'success'   },
                        { label: 'Total del pago',     value: informacion?.total != null ? formatNumber(informacion.total, 2, '$', '') : '-', icon: 'fa-dollar-sign', color: 'success' },
                    ].map(({ label, value, icon, color }) => (
                        <div className="col-md" key={label}>
                            <div className="text-center p-3 rounded bg-light">
                                <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-2`} style={{ width: 40, height: 40 }}>
                                    <i className={`fas ${icon} text-${color}`}></i>
                                </div>
                                <div className="text-muted small">{label}</div>
                                <div className="fw-bold">{value}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Ventas ambulantes incluidas */}
            <div className="col-12">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-redo text-success me-2"></i>
                            Ventas ambulantes incluidas
                            {informacion?.vueltas?.length > 0 && (
                                <span className="badge bg-success rounded-pill ms-2">{informacion.vueltas.length}</span>
                            )}
                        </h6>
                    </div>
                    <div className="card-body p-0">
                        <DataTable
                            columns={columnsVueltas}
                            data={informacion?.vueltas || []}
                            pagination
                            paginationPerPage={20}
                            paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                            highlightOnHover
                            noDataComponent="No hay ventas ambulantes en este pago"
                        />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default InformacionPago;
